import { useState, useCallback } from 'react';
import type { ToastMessage } from '../components/ui/Toast';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastOptions {
  title: string;
  message?: string;
  duration?: number;
}

/**
 * Toast notification queue. Error toasts persist longer (6s) and warnings
 * slightly longer (5s) than success/info to ensure users notice problems.
 * IDs use timestamp + random suffix to avoid collisions on rapid triggers.
 */
export function useToast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((type: ToastType, options: ToastOptions) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: ToastMessage = {
      id,
      type,
      ...options,
    };
    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback(
    (title: string, message?: string) => addToast('success', { title, message }),
    [addToast]
  );

  const error = useCallback(
    (title: string, message?: string) => addToast('error', { title, message, duration: 6000 }),
    [addToast]
  );

  const info = useCallback(
    (title: string, message?: string) => addToast('info', { title, message }),
    [addToast]
  );

  const warning = useCallback(
    (title: string, message?: string) => addToast('warning', { title, message, duration: 5000 }),
    [addToast]
  );

  return {
    toasts,
    dismissToast,
    success,
    error,
    info,
    warning,
  };
}
